import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, FileSpreadsheet, FileDown } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { downloadTransactionsXlsx } from "@/lib/xlsxTransactions";
import { downloadTransactionsPdf } from "@/lib/pdfTransactions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { type: "income", amount: "", category: "", description: "", date: "", bank_account_id: "none", cari_id: "none" };

export default function Transactions() {
  const { t, fmt, fmtDate } = useI18n();
  const { canEdit, user } = useAuth();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [banks, setBanks] = useState([]);
  const [caris, setCaris] = useState([]);
  const [filter, setFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const load = async () => {
    const [tx, b, c] = await Promise.all([api.get("/transactions"), api.get("/bank-accounts"), api.get("/cari")]);
    setRows(tx.data);
    setBanks(b.data);
    setCaris(c.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const cats = form.type === "income" ? t.catIncome : t.catExpense;
  const filtered = useMemo(
    () => rows.filter((r) => (filter === "all" || r.type === filter) && (!from || r.date >= from) && (!to || r.date <= to)),
    [rows, filter, from, to]
  );

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, date: new Date().toISOString().slice(0, 10) });
    setError("");
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...r, bank_account_id: r.bank_account_id || "none", cari_id: r.cari_id || "none" });
    setError("");
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      bank_account_id: form.bank_account_id === "none" ? null : form.bank_account_id,
      cari_id: form.cari_id === "none" ? null : form.cari_id,
    };
    try {
      if (editing) await api.put(`/transactions/${editing.id}`, payload);
      else await api.post("/transactions", payload);
      setOpen(false);
      load().catch(() => {});
    } catch (e) {
      setError(fmtErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t.app.confirmDelete)) return;
    await api.delete(`/transactions/${id}`);
    load().catch(() => {});
  };

  const filters = [
    { k: "all", label: t.app.all },
    { k: "income", label: t.app.income },
    { k: "expense", label: t.app.expense },
  ];

  return (
    <div className="space-y-5" data-testid="transactions-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center rounded-full border border-slate-200 bg-white p-1" data-testid="transactions-filter">
          {filters.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`transactions-filter-${f.k}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                filter === f.k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-36 bg-white"
            aria-label={t.txn.from} title={t.txn.from} data-testid="txn-from-input" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-36 bg-white"
            aria-label={t.txn.to} title={t.txn.to} data-testid="txn-to-input" />
          <Button variant="outline" disabled={!filtered.length || pdfBusy}
            onClick={async () => { setPdfBusy(true); try { await downloadTransactionsPdf({ t, fmt, rows: filtered, from, to, userName: user?.name }); } finally { setPdfBusy(false); } }}
            data-testid="txn-pdf-button">
            <FileDown className="mr-2 h-4 w-4" />{pdfBusy ? t.pdf.preparing : t.pdf.download}
          </Button>
          <Button variant="outline" onClick={() => downloadTransactionsXlsx({ t, rows: filtered })}
            disabled={!filtered.length} data-testid="txn-excel-button">
            <FileSpreadsheet className="mr-2 h-4 w-4" />{t.txn.excel}
          </Button>
          {canEdit && (
            <Button onClick={openNew} data-testid="txn-new-button">
              <Plus className="mr-2 h-4 w-4" />{t.txn.new}
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm" data-testid="txn-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left font-mono text-[10px] uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3.5 font-medium">{t.app.date}</th>
              <th className="px-5 py-3.5 font-medium">{t.app.category}</th>
              <th className="hidden px-5 py-3.5 font-medium md:table-cell">{t.app.description}</th>
              <th className="hidden px-5 py-3.5 font-medium lg:table-cell">{t.app.cari}</th>
              <th className="hidden px-5 py-3.5 font-medium lg:table-cell">{t.app.bank}</th>
              <th className="px-5 py-3.5 text-right font-medium">{t.app.amount}</th>
              <th className="px-5 py-3.5 text-right font-medium">{t.app.actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-14 text-center text-sm text-slate-400">{t.app.noData}</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="transition-colors hover:bg-slate-50" data-testid={`txn-row-${r.id}`}>
                <td className="whitespace-nowrap px-5 py-3.5 font-mono text-xs text-slate-500">{fmtDate(r.date)}</td>
                <td className="px-5 py-3.5">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                    r.type === "income" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
                  }`}>
                    {r.category}
                  </span>
                </td>
                <td className="hidden max-w-[240px] truncate px-5 py-3.5 text-slate-600 md:table-cell">{r.description || "—"}</td>
                <td className="hidden px-5 py-3.5 text-slate-500 lg:table-cell">{r.cari_name || "—"}</td>
                <td className="hidden px-5 py-3.5 text-slate-500 lg:table-cell">{r.bank_account_name || "—"}</td>
                <td className={`tnum whitespace-nowrap px-5 py-3.5 text-right font-mono font-medium ${r.type === "income" ? "text-positive" : "text-negative"}`}>
                  {r.type === "income" ? "+" : "−"}{fmt(r.amount)}
                </td>
                <td className="whitespace-nowrap px-5 py-3.5 text-right">
                  {canEdit && (
                    <>
                      <button onClick={() => openEdit(r)} aria-label={t.app.edit} data-testid={`txn-edit-${r.id}`}
                        className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(r.id)} aria-label={t.app.delete} data-testid={`txn-delete-${r.id}`}
                        className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="txn-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t.txn.edit : form.type === "income" ? t.txn.newIncome : t.txn.newExpense}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.app.income} / {t.app.expense}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category: "" })} data-testid="txn-type-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">{t.app.income}</SelectItem>
                    <SelectItem value="expense">{t.app.expense}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.app.amount}</Label>
                <Input type="number" step="0.01" min="0" required value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="txn-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.app.category}</Label>
                <Select value={form.category || undefined} onValueChange={(v) => setForm({ ...form, category: v })} data-testid="txn-category-select">
                  <SelectTrigger><SelectValue placeholder={t.app.category} /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.app.date}</Label>
                <Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="txn-date-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t.app.description}</Label>
              <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="txn-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.app.bank}</Label>
                <Select value={form.bank_account_id} onValueChange={(v) => setForm({ ...form, bank_account_id: v })} data-testid="txn-bank-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.app.cari}</Label>
                <Select value={form.cari_id} onValueChange={(v) => setForm({ ...form, cari_id: v })} data-testid="txn-cari-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {caris.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {error && <p className="rounded-md border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm text-red-600" data-testid="txn-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="txn-cancel-button">{t.app.cancel}</Button>
            <Button onClick={save} disabled={!form.amount || !form.category} data-testid="txn-save-button">{t.app.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
